import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomButton from "../../components/CustomButton";
import Api from "../../services/Api";
import { VisionStatementInterface } from "../DevelopmentPlan/interfaces";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../components/Modal"
import CloseIcon from "../../components/Icons/CloseIcon";
import { ChevronDown as IconDown, Plus as PlusIcon, } from "flowbite-react-icons/outline";
import {PaperPlane as SendIcon} from "flowbite-react-icons/solid";
import AiIcon from "../../components/Icons/AiIcon";
import InformationCircleIcon from "../../components/Icons/InformationCircleIcon";
import animationProcessing from "url:shared-assets/animations/processing.svg";
import {getLanguage} from "../../lang/helpers";

interface Props {
    visionStatement: VisionStatementInterface;
    save: Function;
    close: () => void;
    showDetailModal: Function;
}

interface Question {
    id: string;
    text: string;
    freeText: boolean;
}

const SelfAssessmentSurveyCreation = (props: Props) => {
    const api = Api.getInstance();
    const { t, i18n } = useTranslation();
    const lang = getLanguage(i18n.language);
    const [questions, setQuestions] = useState<Array<Question>>([]);
    const [openAIIndex, setOpenAIIndex] = useState<number | null>(null);
    const [aiInstruction, setAiInstruction] = useState("");
    const [questionLoading, setQuestionLoading] = useState(-1);

    const surveyRequest = props.visionStatement.surveyRequests[0];
    const surveyLanguage = surveyRequest.surveyResult.languages[0];

    useEffect(() => {
        const _questions: Array<Question> = [];
        for (const q of surveyRequest.surveyResult.focusAreas[0].questions) {
            _questions.push({
                text: q.name,
                freeText: true,
                id: q.id,
            })
        }
        setQuestions(_questions);
    }, []);

    const saveSurvey = () => {
        const data = {
            "questions": questions
        }

        api.post('client/vision_statement_edit_survey_questions/' + props.visionStatement.id, data).then(response => {
            if (response.json.status === "ok") {
                props.save();
                props.close();
            } else {
                alert("Error creating Survey");
            }
        })
    }

    // Edit Single question
    const getAiQuestion = (index: number, instruction: string, questionText: string) => {
        const _questions = [];
        if (instruction === "add_question") {
            for (const q of questions) {
                _questions.push(q.text);
            }
        }
        const data = {
            instruction: instruction,
            question: questionText,
            questions: _questions
        };
        setQuestionLoading(index);

        api.post('client/ai_survey_edit_question/' + props.visionStatement.surveyId, data).then(response => {
            if (response.json.status === "ok") {
                const answer = response.json.answer;
                setQuestions((prev) => {
                    const updated = [...prev];
                    updated[index] = { ...updated[index], text: answer };
                    return updated;
                });
                setAiInstruction("");
            } else if (response.json.error) {
                alert(response.json.error);
            } else {
                alert("Unknown error");
            }
            setQuestionLoading(-1);
        });
    };

    const updateQuestions = (index: number, text: string) => {
        const _questions = [...questions];
        _questions[index] = { ..._questions[index], text };
        setQuestions(_questions);
    }
    const addQuestion = () => {
        let index = questions.length;
        const newQuestion: Question = {
            id: "",
            text: "",
            freeText: false,
        };
        setQuestions((prev) => [...prev, newQuestion]);
        getAiQuestion(index, "add_question", "")
    };

    const deleteQuestion = (nr: number) => {
        setQuestions(prev => prev.filter((_, i) => i !== nr));
    }

    const toggleAI = (index: number) => {
        setOpenAIIndex(openAIIndex === index ? null : index);
    };

    const aiQuickActions = [
        { key: "rephrase", label: t("rephrase") },
        { key: "shorten", label: t("shorten") },
        { key: "make-more-specific", label: t("make-more-specific") },
    ];

    return (
      <Modal isOpen={true} onClose={props.close} size="wide">
          <ModalHeader onClose={props.close}>
              <div className="flex justify-between items-center">
                  <div>{t("btn-edit-questions")}</div>
                  <CustomButton
                    buttonType="transparentBase"
                    size="sm"
                    onClick={() => props.showDetailModal()}
                    leftIcon={<InformationCircleIcon alt="info" />}
                    style={{ width: "unset" }}
                  >
                      {t("long-term-goal-details")}
                  </CustomButton>
              </div>

          </ModalHeader>
          <ModalBody>
              <div>All questions will be using the same answer options:</div>
              <ul className="mt-2 flex flex-col gap-1 list-disc list-inside">
                  {surveyRequest.surveyResult.answerOptions[surveyLanguage]
                    .filter((o, i) => i > 0).map((option, index) => (
                    <li key={index}>{option}</li>
                  ))}
                  <li>
                      {surveyRequest.surveyResult.answerOptions[surveyLanguage][0]}
                      &nbsp;(
                      {lang === "sv" ? "bara för dem du ber om feedback från" : "only for feedback request recipients"}
                      )
                  </li>
              </ul>
              <div className="mt-4">
                  {questions.map((question, index) => (
                    <div key={"question_" + index} className="mb-2 pb-4 bg-indigo-800 border-indigo-700 border rounded-md">
                        <div className="flex justify-between items-center p-4">
                            <div className="z-title1 text-white">
                                {t("question")} {index + 1}
                            </div>
                            <CloseIcon aria-label="Close question" onClick={() => deleteQuestion(index)}
                                        className="h-6 w-6 p-1 hover:bg-indigo-700 rounded-full cursor-pointer text-white!" />

                        </div>

                        <div className="px-4">
                            {
                                questionLoading === index ? (
                                  <div style={{height: 54, padding: 24}}>
                                      <img
                                        src={animationProcessing}
                                        className="mx-auto"
                                        alt="loading"
                                        style={{height: 16, width: 114}}
                                      />
                                  </div>
                                ) : (
                                  <input type="text" className="z-input" value={question.text} placeholder={t("question")}
                                         onChange={(e) => updateQuestions(index, e.target.value)} />
                                )
                            }

                        </div>

                        <hr className="border-indigo-700 my-4" />

                        <div
                          className="flex justify-between items-center cursor-pointer px-4"
                          onClick={() => toggleAI(index)}
                        >
                            <div className="flex gap-x-2 items-center">
                                  <AiIcon alt="ai" className="h-5 w-5"/>
                                <div className="text-white">{t("improve-with-AI")}</div>
                            </div>

                             <IconDown aria-hidden="true"
                                     className={`h-6 w-6 p-1 hover:bg-indigo-700 rounded-full text-white! ${openAIIndex === index ? "rotate-180" : ""}`} />

                        </div>

                        {openAIIndex === index && (
                          <div className="px-4">
                              <div className="flex gap-x-1 mt-1 text-white text-sm">
                                  {aiQuickActions.map((action) => (
                                    <CustomButton
                                      key={action.key}
                                      buttonType="transparentBase"
                                      size="sm"
                                      onClick={() =>
                                        getAiQuestion(index, action.key, question.text)
                                      }
                                      style={{ width: "unset" }}
                                    >
                                        {action.label}
                                    </CustomButton>
                                  ))}
                              </div>

                              <div className="flex items-center mt-2 gap-2 bg-indigo-800 border border-indigo-600 rounded-md px-3 py-2">
                                  <input
                                    type="text"
                                    placeholder={t("describe-desired-changes")}
                                    className="flex-1 text-white placeholder-gray-400 outline-none text-sm"
                                    value={aiInstruction}
                                    onChange={(e) => setAiInstruction(e.target.value)}
                                  />
                                  <CustomButton
                                    buttonType="transparentBase"
                                    size="sm"
                                    onClick={() => getAiQuestion(index, aiInstruction, question.text)}
                                    leftIcon={<SendIcon className="w-10 h-10 p-2 text-white! rotate-90" aria-hidden="true" />}
                                    style={{ width: "unset" }}
                                    className="p-1! gap-x-0!"
                                  />

                              </div>
                          </div>
                        )}
                    </div>
                  ))}
              </div>
              <div className="mt-3">
                  <CustomButton
                    buttonType="neutralBase"
                    size="sm"
                    leftIcon={<PlusIcon className="stroke-2 text-white!" aria-hidden="true" />}
                    onClick={addQuestion}
                  >
                      {t("add")}
                  </CustomButton>
              </div>
          </ModalBody>
          <ModalFooter>
              <div className="flex items-center gap-3 w-full">
                  <CustomButton
                    buttonType="outlineWhiteBase"
                    className="flex-1 cursor-pointer"
                    size="sm"
                    onClick={props.close}
                  >
                      {t("cancel")}
                  </CustomButton>
                  <CustomButton
                    buttonType="blueBase"
                    className="flex-1 cursor-pointer"
                    size="sm"
                    disabled={questions.length === 0}
                    onClick={() => saveSurvey()}
                  >
                      {t("save")}
                  </CustomButton>
              </div>
          </ModalFooter>
      </Modal>
    )
}

export default SelfAssessmentSurveyCreation;